import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import type { Funnel, FunnelDef, FunnelStep } from "@backstory/protocol";
import { useAuth } from "../../../auth/store";
import { useCan } from "../../../auth/roles";
import { EmptyListMark, EmptyState, ErrorState, Skeleton } from "../../../components/States";
import { allowsFeature, useOrgEntitlements } from "../../billing/entitlements";
import { ConfirmDialog, Dialog, Field, PageHeader, RowMenu, Switch, useToast } from "../../../components/ui";
import { countOf, formatDuration, formatRelative } from "../../../lib/format";
import { insightsApi, insightsKeys, type TimeRange } from "../api";
import { useSlowPending, WakingNumbers } from "../WakingNumbers";
import { pct } from "../charts";
import { Card, RangePicker, replayHref, useRange } from "../components";
import { aiApi, aiKeys, useAiCapability, useDeepAnalysis, AiBlockedTooltip, AiOutput, AiSparkle, AiUnavailableNote, JobStatus, Rejected } from "../../ai";
import { settingsEqual, UnsavedGuard } from "../../../components/UnsavedChanges";

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

export function emptyStep(type: FunnelStep["match"]["type"] = "route"): FunnelStep {
  if (type === "route") return { name: "", match: { type: "route", route: "" } };
  if (type === "custom") return { name: "", match: { type: "custom", name: "" } };
  return { name: "", match: { type: "click", selector: "" } };
}

/** The API accepts 2 to 12 steps and a window of up to 7 days. */
export const MAX_FUNNEL_STEPS = 12;
export const MAX_WINDOW_MINUTES = 7 * 24 * 60;

export function validateFunnel(def: FunnelDef): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!def.name.trim()) errors.name = "Give the funnel a name.";
  if (def.steps.length < 2) errors.steps = "A funnel needs at least two steps.";
  if (def.steps.length > MAX_FUNNEL_STEPS) errors.steps = `A funnel can have up to ${MAX_FUNNEL_STEPS} steps.`;
  def.steps.forEach((s, i) => {
    const m = s.match;
    const value = m.type === "route" ? m.route : m.type === "custom" ? m.name : m.selector;
    if (!value.trim()) errors[`step${i}`] = m.type === "route" ? "Enter a route." : m.type === "custom" ? "Enter the event name." : "Enter a selector.";
  });
  if (!(def.windowMinutes > 0)) errors.windowMinutes = "Window must be a positive number of minutes.";
  else if (def.windowMinutes > MAX_WINDOW_MINUTES) errors.windowMinutes = `Window can be up to 7 days (${MAX_WINDOW_MINUTES.toLocaleString()} minutes).`;
  return errors;
}

export function stepValue(s: FunnelStep): string {
  const m = s.match;
  return m.type === "route" ? m.route : m.type === "custom" ? m.name : m.selector;
}

/**
 * A step is named after what it matches when nobody typed a name, so a saved
 * funnel always reads as steps rather than as failing the API's name check.
 */
export function finishFunnel(def: FunnelDef): FunnelDef {
  return {
    ...def,
    name: def.name.trim(),
    steps: def.steps.map((s) => ({ ...s, name: s.name.trim() || stepValue(s).trim(), match: withValue(s, stepValue(s).trim()).match })),
  };
}
function withValue(s: FunnelStep, v: string): FunnelStep {
  const m = s.match;
  if (m.type === "route") return { ...s, match: { type: "route", route: v } };
  if (m.type === "custom") return { ...s, match: { type: "custom", name: v } };
  return { ...s, match: { type: "click", selector: v } };
}

export function FunnelBuilder({ open, onClose, initial, editing, onSave, saving }: { open: boolean; onClose: () => void; initial?: FunnelDef; editing?: boolean; onSave: (def: FunnelDef) => void; saving?: boolean }) {
  // A new funnel counts steps in order with other pages allowed between them;
  // "straight through" is the stricter reading, chosen deliberately.
  const [baseline] = useState<FunnelDef>(() => (initial ? { name: initial.name, steps: initial.steps, windowMinutes: initial.windowMinutes, strictOrder: initial.strictOrder } : { name: "", steps: [emptyStep(), emptyStep()], windowMinutes: 60, strictOrder: false }));
  const [def, setDef] = useState<FunnelDef>(baseline);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // A suggested funnel is already a complete unsaved draft even before the
  // operator edits it; an existing funnel is dirty only after a change.
  const dirty = (!editing && !!initial) || !settingsEqual(def, baseline);
  const requestClose = () => {
    if (dirty && !window.confirm("Discard unsaved funnel changes?")) return;
    onClose();
  };
  const submit = () => {
    const e = validateFunnel(def);
    setErrors(e);
    if (Object.keys(e).length === 0) onSave(finishFunnel(def));
  };
  const setStep = (i: number, s: FunnelStep) => setDef((d) => ({ ...d, steps: d.steps.map((x, j) => (j === i ? s : x)) }));
  return (
    <Dialog
      open={open}
      onClose={requestClose}
      title={editing ? "Edit funnel" : "New funnel"}
      width={640}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={requestClose}>Cancel</button>
          <button type="button" className="btn primary" onClick={submit} disabled={saving || !dirty}>{saving ? "Saving…" : "Save funnel"}</button>
        </>
      }
    >
      <UnsavedGuard dirty={dirty} />
      <Field label="Name" error={errors.name}>{(p) => <input {...p} className="input" value={def.name} onChange={(e) => setDef({ ...def, name: e.target.value })} placeholder="Intake completion" />}</Field>
      <div className="field">
        <label>Steps</label>
        {errors.steps && <div className="error" role="alert">{errors.steps}</div>}
        <ol className="fn-steps">
          {def.steps.map((s, i) => (
            <li key={i} className="fn-step">
              <span className="n" aria-hidden="true">{i + 1}</span>
              <input className="input fn-step-name" aria-label={`Step ${i + 1} name`} placeholder="Step name (optional)" value={s.name} onChange={(e) => setStep(i, { ...s, name: e.target.value })} autoComplete="off" />
              <select className="input fn-step-type" aria-label={`Step ${i + 1} type`} value={s.match.type} onChange={(e) => setStep(i, { ...emptyStep(e.target.value as FunnelStep["match"]["type"]), name: s.name })} autoComplete="off">
                <option value="route">Route</option><option value="custom">Custom event</option><option value="click">Click</option>
              </select>
              <button type="button" className="btn ghost icon-btn" aria-label={`Remove step ${i + 1}`} onClick={() => setDef((d) => ({ ...d, steps: d.steps.filter((_, j) => j !== i) }))} disabled={def.steps.length <= 2}>✕</button>
              <input className="input match" aria-label={`Step ${i + 1} ${s.match.type === "route" ? "route" : s.match.type === "custom" ? "event name" : "selector"}`} aria-invalid={errors[`step${i}`] ? true : undefined} placeholder={s.match.type === "route" ? "/orders/:id" : s.match.type === "custom" ? "checkout_completed" : "button[data-testid=submit]"} value={stepValue(s)} onChange={(e) => setStep(i, withValue(s, e.target.value))} autoComplete="off" />
              {errors[`step${i}`] && <div className="error match" role="alert">{errors[`step${i}`]}</div>}
            </li>
          ))}
        </ol>
        <div className="fn-steps-foot">
          <button type="button" className="btn fn-add-step" onClick={() => setDef((d) => ({ ...d, steps: [...d.steps, emptyStep()] }))} disabled={def.steps.length >= MAX_FUNNEL_STEPS}>+ Add step</button>
          <span className="hint">A route matches its page. Numbers and ids in a path are grouped, so <code>/orders/88</code> and <code>/orders/:id</code> are the same step.</span>
        </div>
      </div>
      <div className="fn-settings">
        <Field label="Window" hint="Minutes from the first to the last step, up to 7 days." error={errors.windowMinutes}>{(p) => <input {...p} className="input narrow" type="number" min={1} max={MAX_WINDOW_MINUTES} value={def.windowMinutes} onChange={(e) => setDef({ ...def, windowMinutes: Number(e.target.value) })} />}</Field>
        <Switch checked={def.strictOrder} onChange={(v) => setDef({ ...def, strictOrder: v })} label="Straight through" hint="Count only sessions that go from each step directly to the next. Off, steps still happen in order and other pages or events may come between them." />
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Suggestions
// ---------------------------------------------------------------------------

/**
 * A project with no funnels has nothing to look at and an empty builder to
 * fill in, so offer the journeys sessions already walk. These are drafts from
 * recorded traffic, not guesses: picking one opens the builder with its steps.
 */
function SuggestedFunnels({
  onUse,
  emptyMessage = "No funnels yet. Create one to see where users drop off.",
  heading = true,
}: {
  onUse: (def: FunnelDef) => void;
  emptyMessage?: string;
  heading?: boolean;
}) {
  const auth = useAuth();
  const q = useQuery({ queryKey: insightsKeys.funnelSuggestions(auth.currentProjectId), queryFn: insightsApi.funnelSuggestions });
  const slowLoad = useSlowPending(q.isPending);
  if (q.isPending) return slowLoad ? <WakingNumbers /> : <Skeleton lines={4} />;
  if (q.isError) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.data!.length === 0) {
    return (
      <p className="muted" style={{ textAlign: "center", padding: 24 }} data-testid="funnels-empty">
        {emptyMessage}
      </p>
    );
  }
  return (
    <div className="section" data-testid="funnel-suggestions">
      {heading && <h2 className="h3">Journeys your sessions already walk</h2>}
      <p className="muted small">Drafted from the last 7 days of recorded traffic. Nothing is saved until you save it.</p>
      <div className="fn-suggestions">
        {q.data!.map((s, i) => (
          <div key={i} className="card section fn-suggestion" data-testid="funnel-suggestion">
            <div>
              <strong>{s.def.name}</strong>
              <div className="small muted">{s.def.steps.map((st) => st.name).join(" → ")}</div>
              <div className="small muted">{s.sessions.toLocaleString()} sessions walked this path</div>
            </div>
            <button type="button" className="btn" onClick={() => onUse(s.def)}>Use this</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plan
// ---------------------------------------------------------------------------

function FunnelPlan({ title, children }: { title: string; children: ReactNode }) {
  const auth = useAuth();
  const can = useCan();
  const ent = useOrgEntitlements(auth.currentOrgId);
  if (!ent.isSuccess && !ent.isError) {
    return (
      <>
        <PageHeader title={title} />
        <Skeleton lines={6} />
      </>
    );
  }
  if (ent.isSuccess && !allowsFeature(ent.data, "funnels")) {
    return (
      <>
        <PageHeader title={title} />
        <div className="card">
          <EmptyState
            mark={<EmptyListMark />}
            title="Funnels are on Team and above"
            hint="Team and above show the steps people take, where they leave, and how long each step takes."
            action={
              can.billing && auth.currentOrgId ? (
                <Link to="/orgs/$org/billing" params={{ org: auth.currentOrgId }} className="btn primary">
                  See plans
                </Link>
              ) : undefined
            }
          />
        </div>
      </>
    );
  }
  return children;
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export function FunnelsPage() {
  return (
    <FunnelPlan title="Funnels">
      <FunnelsList />
    </FunnelPlan>
  );
}

function FunnelsList() {
  const auth = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: insightsKeys.funnels(auth.currentProjectId), queryFn: insightsApi.funnels });
  const [open, setOpen] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [editing, setEditing] = useState<Funnel | undefined>();
  const [draft, setDraft] = useState<FunnelDef | undefined>();
  const [deleting, setDeleting] = useState<Funnel | null>(null);
  const start = (d?: FunnelDef) => { setEditing(undefined); setDraft(d); setOpen(true); };
  const close = () => { setOpen(false); setEditing(undefined); setDraft(undefined); };
  const save = useMutation({
    mutationFn: (def: FunnelDef) => (editing ? insightsApi.patchFunnel(editing.id, def) : insightsApi.createFunnel(def)),
    onSuccess: () => { toast.push("success", editing ? "Funnel updated" : "Funnel created"); close(); void qc.invalidateQueries({ queryKey: ["insights", "funnels"] }); },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const del = useMutation({
    mutationFn: (id: string) => insightsApi.deleteFunnel(id),
    onSuccess: () => { setDeleting(null); toast.push("success", "Funnel deleted"); void qc.invalidateQueries({ queryKey: ["insights", "funnels"] }); },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  return (
    <>
      <PageHeader title="Funnels" actions={
        can.write && (
          <div className="actions-row">
            <button type="button" className="btn" onClick={() => setSuggestionsOpen(true)}>Suggest funnels</button>
            <button type="button" className="btn primary" onClick={() => start()}>New funnel</button>
          </div>
        )
      } />
      {q.isError ? (
        <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>
      ) : (
        <div className="card">
          <table className="table fn-table" aria-label="Funnels" aria-busy={q.isPending || undefined}>
            <thead><tr><th>Name</th><th>Steps</th><th className="fn-col-extra">Window</th><th className="fn-col-extra">Path</th><th className="fn-col-extra">Created</th><th></th></tr></thead>
            <tbody>
              {(q.data ?? []).map((f) => (
                <tr key={f.id} className="clickable" tabIndex={0} data-testid="funnel-row" onClick={() => void navigate({ to: "/funnels/$id", params: { id: f.id } })} onKeyDown={(e) => e.key === "Enter" && void navigate({ to: "/funnels/$id", params: { id: f.id } })}>
                  <td className="fn-cell-name"><span className="ins-title">{f.name}</span></td>
                  <td className="small muted fn-cell-steps">{f.steps.map((s) => s.name || stepValue(s)).join(" → ")}</td>
                  <td className="mono small fn-col-extra">{f.windowMinutes} min</td>
                  <td className="small fn-col-extra">{f.strictOrder ? "straight through" : "in order"}</td>
                  <td className="small muted fn-col-extra">{formatRelative(f.createdAt)}</td>
                  <td className="fn-cell-menu" onClick={(e) => e.stopPropagation()}>
                    {can.write && <RowMenu label={`Actions for ${f.name}`}>
                      <button type="button" role="menuitem" onClick={() => { setDraft(undefined); setEditing(f); setOpen(true); }}>Edit</button>
                      <button type="button" role="menuitem" className="danger" onClick={() => setDeleting(f)}>Delete</button>
                    </RowMenu>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!q.isPending && (q.data?.length ?? 0) === 0 && (can.write ? <SuggestedFunnels onUse={start} /> : (
            <p className="muted" style={{ textAlign: "center", padding: 24 }} data-testid="funnels-empty">No funnels yet. An editor or admin can create one.</p>
          ))}
        </div>
      )}
      {suggestionsOpen && (
        <Dialog open onClose={() => setSuggestionsOpen(false)} title="Suggested funnels" width={700}>
          <SuggestedFunnels
            heading={false}
            emptyMessage="No new journeys qualify yet. Check again after more traffic is recorded."
            onUse={(def) => { setSuggestionsOpen(false); start(def); }}
          />
        </Dialog>
      )}
      {open && <FunnelBuilder key={editing?.id ?? draft?.name ?? "new"} open={open} onClose={close} initial={editing ?? draft} editing={!!editing} onSave={(d) => save.mutate(d)} saving={save.isPending} />}
      <ConfirmDialog
        open={!!deleting}
        danger
        title="Delete funnel"
        confirmLabel={del.isPending ? "Deleting…" : "Delete funnel"}
        busy={del.isPending}
        onConfirm={() => deleting && del.mutate(deleting.id)}
        onClose={() => setDeleting(null)}
      >
        <p>This permanently deletes {deleting?.name}.</p>
      </ConfirmDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export function FunnelDetailPage() {
  return (
    <FunnelPlan title="Funnel">
      <FunnelDetail />
    </FunnelPlan>
  );
}

function FunnelDetail() {
  const { id } = useParams({ strict: false }) as { id: string };
  const auth = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const toast = useToast();
  const { range } = useRange();
  const queryClient = useQueryClient();
  const funnels = useQuery({ queryKey: insightsKeys.funnels(auth.currentProjectId), queryFn: insightsApi.funnels });
  const funnel = funnels.data?.find((f) => f.id === id);
  const q = useQuery({ queryKey: insightsKeys.funnelResults(id, range), queryFn: () => insightsApi.funnelResults(id, range) });
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const save = useMutation({
    mutationFn: (def: FunnelDef) => insightsApi.patchFunnel(id, def),
    onSuccess: () => {
      toast.push("success", "Funnel updated");
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ["insights", "funnels"] });
      void queryClient.invalidateQueries({ queryKey: ["insights", "funnelResults", id] });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const del = useMutation({
    mutationFn: () => insightsApi.deleteFunnel(id),
    onSuccess: () => {
      toast.push("success", "Funnel deleted");
      void queryClient.invalidateQueries({ queryKey: ["insights", "funnels"] });
      void navigate({ to: "/funnels" });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const aiCap = useAiCapability();
  const explanationKey = useMemo(() => aiKeys.funnelExplanation(id, range), [id, range.from, range.to]);
  const savedExplanation = useQuery({ queryKey: explanationKey, queryFn: () => aiApi.funnelExplanation(id, range), retry: false });
  const forceExplanation = useRef(false);
  const explainDrop = useDeepAnalysis(useCallback(() => aiApi.explainFunnel(id, range, forceExplanation.current), [id, range]));
  const currentExplanation = explainDrop.state.phase === "done" ? explainDrop.state.artifact : undefined;
  const explanation = currentExplanation ?? savedExplanation.data ?? undefined;
  const aiSteps = explanation?.items;
  useEffect(() => {
    if (currentExplanation) queryClient.setQueryData(explanationKey, currentExplanation);
  }, [currentExplanation, explanationKey, queryClient]);
  const slowLoad = useSlowPending(q.isPending);
  if (q.isPending) {
    return (
      <>
        <PageHeader back={<Link to="/funnels" className="btn ghost">← Funnels</Link>} title={funnel?.name ?? "Funnel"} actions={<RangePicker />} />
        {slowLoad ? <WakingNumbers /> : <Skeleton lines={8} />}
      </>
    );
  }
  if (q.isError) return <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>;
  const r = q.data;
  const first = r.steps[0]?.sessions ?? 0;
  const overall = first ? ((r.steps[r.steps.length - 1]?.sessions ?? 0) / first) * 100 : 0;
  const matchOf = (i: number) => (funnel?.steps[i] ? stepValue(funnel.steps[i]!) : undefined);
  return (
    <>
      <PageHeader
        back={<Link to="/funnels" className="btn ghost">← Funnels</Link>}
        title={funnel?.name ?? "Funnel"}
        subtitle={<>{r.steps.length} steps · overall conversion <strong>{pct(overall)}</strong>{funnel ? <span className="muted"> · window {funnel.windowMinutes} min · {funnel.strictOrder ? "straight through" : "in order"}</span> : null}</>}
        actions={
          <div className="actions-row">
            <RangePicker />
            {can.write && funnel && (
              <RowMenu label="Funnel actions">
                <button type="button" role="menuitem" onClick={() => setEditing(true)}>Edit</button>
                <button type="button" role="menuitem" className="danger" onClick={() => setDeleting(true)}>Delete</button>
              </RowMenu>
            )}
          </div>
        }
      />
      <Card
        title="Conversion by step"
        actions={
          <AiBlockedTooltip ai={aiCap}>
            <button type="button" className="btn ai-btn" onClick={() => { forceExplanation.current = !!explanation; void explainDrop.start(); }} disabled={explainDrop.busy || aiCap.blocked || first === 0} title={first === 0 ? "No sessions to analyze in this period" : aiCap.titleFor(explanation ? "Run a new AI analysis for this range" : "Ask AI why sessions dropped here")} aria-label={`${explanation ? "Re-explain" : "Explain"} with AI`} data-testid="explain-drop">
              <AiSparkle />
              {explainDrop.busy ? "Analyzing…" : explanation ? "Re-explain" : "Explain"}
            </button>
          </AiBlockedTooltip>
        }
      >
        {first === 0 && (
          <p className="muted small fn-empty" data-testid="funnel-empty">
            No sessions reached <strong>{r.steps[0]?.name}</strong> in this period. Try a longer period, or check that the first step matches a page or event your app records.
          </p>
        )}
        <div className="fn-result viz" role="img" aria-label={`Funnel: ${r.steps.map((s) => `${s.name} ${countOf(s.sessions, "session")}`).join(", ")}`}>
          {r.steps.map((s, i) => {
            const width = first ? (s.sessions / first) * 100 : 0;
            const ai = aiSteps?.find((e) => e.stepIndex === i);
            // A session that stalled after step i-1 is what the drop into step i
            // looks like, so the samples come from the step before this one.
            const dropped = i > 0 ? (r.steps[i - 1]?.dropSampleSessionIds ?? []) : [];
            const match = matchOf(i);
            return (
              <div key={`${i}-${s.name}`} className="fn-step-rows" data-testid="funnel-step">
                <div className="fn-row">
                  <span className="fn-label" title={match}><strong>{i + 1}.</strong> {s.name}</span>
                  <div className="track" title={countOf(s.sessions, "session")}>
                    <div className="fill" style={{ width: `${width}%` }} />
                  </div>
                  <span className="meta">
                    {countOf(s.sessions, "session")}{i > 0 && <> · {pct(s.conversionFromPrev * (s.conversionFromPrev <= 1 ? 100 : 1))}</>}
                    {s.medianMsFromPrev !== undefined && <> · {formatDuration(s.medianMsFromPrev)}</>}
                  </span>
                </div>
                {i > 0 && (
                  <div className="fn-drop">
                    <span className="small muted">Dropped: {((r.steps[i - 1]?.sessions ?? 0) - s.sessions).toLocaleString()}</span>
                    {dropped.slice(0, 5).map((sid) => (<Link key={sid} {...replayHref(sid)} className="badge neutral" title="Watch a session that dropped here">{sid.slice(0, 8)}…</Link>))}
                    {ai && <AiOutput className="small fn-ai" testId="funnel-ai" compact>{ai.text}</AiOutput>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <AiUnavailableNote ai={aiCap} />
        {(explainDrop.state.phase === "starting" || explainDrop.state.phase === "running") && <JobStatus job={explainDrop.state.phase === "running" ? explainDrop.state.job : undefined} />}
        {explainDrop.state.phase === "rejected" && <Rejected job={explainDrop.state.job} orgId={auth.currentOrgId} />}
        {explainDrop.state.phase === "failed" && <div className="error-box" role="alert">{explainDrop.state.error}</div>}
        <details className="viz-table-toggle fn-table-view">
          <summary>Table view</summary>
          <table>
            <thead><tr><th>Step</th>{funnel && <th>Matches</th>}<th>Sessions</th><th>Users</th><th>From previous</th><th>Median time</th></tr></thead>
            <tbody>
              {r.steps.map((s, i) => (<tr key={`${i}-${s.name}`}><td>{i + 1}. {s.name}</td>{funnel && <td className="mono small">{matchOf(i) ?? "—"}</td>}<td>{s.sessions.toLocaleString()}</td><td>{s.users.toLocaleString()}</td><td>{i === 0 ? "—" : pct(s.conversionFromPrev * (s.conversionFromPrev <= 1 ? 100 : 1))}</td><td>{s.medianMsFromPrev !== undefined ? formatDuration(s.medianMsFromPrev) : "—"}</td></tr>))}
            </tbody>
          </table>
        </details>
      </Card>
      <VariantCompare id={id} range={range} projectId={auth.currentProjectId} />
      {editing && funnel && <FunnelBuilder key={funnel.id} open onClose={() => setEditing(false)} initial={funnel} editing onSave={(d) => save.mutate(d)} saving={save.isPending} />}
      <ConfirmDialog
        open={deleting}
        danger
        title="Delete funnel"
        confirmLabel={del.isPending ? "Deleting…" : "Delete funnel"}
        busy={del.isPending}
        onConfirm={() => del.mutate()}
        onClose={() => setDeleting(false)}
      >
        <p>This permanently deletes {funnel?.name ?? "this funnel"}.</p>
      </ConfirmDialog>
    </>
  );
}

function VariantCompare({ id, range, projectId }: { id: string; range: TimeRange; projectId?: string }) {
  const features = useQuery({ queryKey: insightsKeys.flags(range, projectId), queryFn: () => insightsApi.flags(range), retry: 0 });
  const [feature, setFeature] = useState("");
  const compare = useQuery({
    queryKey: insightsKeys.funnelCompare(id, range, feature),
    queryFn: () => insightsApi.funnelCompare(id, range, feature),
    enabled: !!feature,
  });
  const slowLoad = useSlowPending(features.isPending || (!!feature && compare.isPending));
  if (features.isError) return null;
  const known = features.data?.some((f) => f.flag === feature);
  return (
    <Card title="Compare by feature">
      <label className="field">
        <span>Feature</span>
        <select className="input" aria-label="Feature" value={feature} onChange={(e) => setFeature(e.target.value)} disabled={features.isPending} autoComplete="off">
          <option value="">{features.data && features.data.length === 0 ? "No features in this period" : "Choose a feature"}</option>
          {(features.data ?? []).map((f) => <option key={f.flag} value={f.flag}>{f.flag}</option>)}
          {feature && !known ? <option value={feature}>{feature}</option> : null}
        </select>
      </label>
      {features.data && features.data.length === 0 ? <p className="muted small">Features appear here after a session records one.</p> : null}
      {slowLoad ? <WakingNumbers /> : feature && compare.isPending ? <Skeleton lines={3} /> : null}
      {feature && compare.isError ? <ErrorState error={compare.error} retry={() => void compare.refetch()} /> : null}
      {compare.data && compare.data.variants.length === 0 ? <p className="muted small">No sessions recorded this feature in this period.</p> : null}
      {compare.data && compare.data.variants.length > 0 ? (
        <div className="variant-grid">
          {compare.data.variants.map((v) => {
            const first = v.steps[0]?.sessions ?? 0;
            const last = v.steps[v.steps.length - 1]?.sessions ?? 0;
            const overall = first ? (last / first) * 100 : 0;
            return (
              <article key={v.value} className="variant-card" data-testid="variant-card">
                <h3>{v.value}</h3>
                <p className="muted small">{pct(overall)} through · {countOf(first, "session")}</p>
                <ol>
                  {v.steps.map((s, i) => <li key={`${i}-${s.name}`}>{s.name} · {countOf(s.sessions, "session")}</li>)}
                </ol>
              </article>
            );
          })}
        </div>
      ) : null}
    </Card>
  );
}
